import { useMemo } from "react";
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
} from "chart.js";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler
);

const hexToRgba = (hex, alpha) => {
  const parsed = hex.replace("#", "");
  const bigint = parseInt(parsed, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const buildLineData = (chart) => ({
  labels: chart.labels || [],
  datasets: (chart.datasets || []).map((dataset) => ({
    label: dataset.label,
    data: dataset.data,
    borderColor: dataset.borderColor,
    backgroundColor: hexToRgba(dataset.backgroundColor, 0.12),
    pointBackgroundColor: dataset.backgroundColor,
    pointBorderColor: "#ffffff",
    pointRadius: 3,
    pointHoverRadius: 5,
    borderWidth: 2.5,
    tension: 0.4,
    fill: true,
    hidden: dataset.hidden,
  })),
});

const buildBarData = (chart) => ({
  labels: chart.labels || [],
  datasets: (chart.datasets || []).map((dataset) => ({
    label: dataset.label,
    data: dataset.data,
    backgroundColor: dataset.backgroundColor,
    borderRadius: 3,
    maxBarThickness: 18,
    hidden: dataset.hidden,
  })),
});

const gridOptions = {
  y: {
    grid: { drawBorder: false, borderDash: [4, 4], color: "#f0f2f5" },
    ticks: { color: "#adb5bd", font: { size: 11, family: "Roboto" } },
  },
  x: {
    grid: { display: false, drawBorder: false },
    ticks: { color: "#adb5bd", font: { size: 11, family: "Roboto" } },
  },
};

const tooltipOptions = {
  backgroundColor: "#ffffff",
  titleColor: "#344767",
  bodyColor: "#344767",
  borderColor: "#dee2e6",
  borderWidth: 1,
  padding: 10,
  boxPadding: 4,
};

const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: "index" },
  plugins: { legend: { display: false }, tooltip: tooltipOptions },
  scales: gridOptions,
};

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: "index" },
  plugins: { legend: { display: false }, tooltip: tooltipOptions },
  scales: gridOptions,
};

function TransactionsTrendCard({ dateRangeLabel, countChart, amountChart }) {
  const lineData = useMemo(() => buildLineData(countChart), [countChart]);
  const barData = useMemo(() => buildBarData(amountChart), [amountChart]);

  return (
    <Card
      sx={({ boxShadows }) => ({
        border: "1px solid",
        borderColor: "grey.200",
        boxShadow: boxShadows.sm,
        overflow: "hidden",
      })}
    >
      <MDBox
        px={3}
        py={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ borderBottom: "1px solid", borderColor: "grey.200" }}
      >
        <MDTypography variant="h6" fontWeight="medium">
          Transactions Trend
        </MDTypography>
        {dateRangeLabel && (
          <MDTypography variant="caption" color="text" fontWeight="regular">
            {dateRangeLabel}
          </MDTypography>
        )}
      </MDBox>

      <Grid container>
        <Grid item xs={12} md={6} sx={{ p: 3 }}>
          <MDTypography
            variant="button"
            color="text"
            fontWeight="medium"
            display="block"
            mb={1.5}
          >
            Transaction Count
          </MDTypography>
          <MDBox height="14rem">
            <Line data={lineData} options={lineOptions} redraw />
          </MDBox>
        </Grid>

        <Grid item xs={12} md={6} sx={{ p: 3 }}>
          <MDTypography
            variant="button"
            color="text"
            fontWeight="medium"
            display="block"
            mb={1.5}
          >
            Transaction Value
          </MDTypography>
          <MDBox height="14rem">
            <Bar data={barData} options={barOptions} redraw />
          </MDBox>
        </Grid>
      </Grid>
    </Card>
  );
}

TransactionsTrendCard.defaultProps = {
  dateRangeLabel: "",
};

TransactionsTrendCard.propTypes = {
  dateRangeLabel: PropTypes.string,
  countChart: PropTypes.shape({
    labels: PropTypes.array,
    datasets: PropTypes.array,
  }).isRequired,
  amountChart: PropTypes.shape({
    labels: PropTypes.array,
    datasets: PropTypes.array,
  }).isRequired,
};

export default TransactionsTrendCard;
